import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Search,
  Trash2,
  Shield,
  FileText,
  AlertTriangle,
  CheckCircle,
  User,
  Briefcase,
  Eye,
  ChevronLeft,
  ChevronRight,
  Copy,
  Download,
} from 'lucide-react';
import { api } from '../api/client';
import { useToast } from '../contexts/ToastContext';
import type { GdprDeletionCertificate } from '../types';

export default function GDPRErasure() {
  const { showToast } = useToast();
  const queryClient = useQueryClient();

  const [searchTerm, setSearchTerm] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCandidateId, setSelectedCandidateId] = useState<number | null>(null);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [forceDelete, setForceDelete] = useState(false);
  const [certificate, setCertificate] = useState<GdprDeletionCertificate | null>(null);
  const [auditPage, setAuditPage] = useState(1);

  // Search candidates
  const { data: searchResults, isLoading: searching } = useQuery({
    queryKey: ['gdpr', 'search', searchQuery],
    queryFn: () => api.searchCandidatesForErasure(searchQuery),
    enabled: searchQuery.length >= 2,
  });

  // Preview candidate data
  const { data: preview, isLoading: loadingPreview } = useQuery({
    queryKey: ['gdpr', 'preview', selectedCandidateId],
    queryFn: () => api.getCandidateErasurePreview(selectedCandidateId!),
    enabled: selectedCandidateId !== null,
  });

  // GDPR audit log
  const { data: audits, isLoading: loadingAudits } = useQuery({
    queryKey: ['gdpr', 'audits', auditPage],
    queryFn: () => api.getDeletionAudits(auditPage, 10),
  });

  // Execute erasure
  const erasureMutation = useMutation({
    mutationFn: () =>
      api.executeGdprErasure(selectedCandidateId!, 'gdpr_request', forceDelete),
    onSuccess: (result) => {
      setCertificate(result.certificate);
      setShowConfirmDialog(false);
      setSelectedCandidateId(null);
      setSearchQuery('');
      setSearchTerm('');
      setForceDelete(false);
      queryClient.invalidateQueries({ queryKey: ['gdpr'] });
      showToast(
        `Erasure complete. ${result.deletionSummary.total} records deleted.`,
        'success'
      );
    },
    onError: (err: Error & { requiresConfirmation?: boolean }) => {
      if (String(err).includes('requiresConfirmation') || String(err).includes('active')) {
        setForceDelete(true);
        showToast(
          'Candidate has active data. Enable force delete to proceed.',
          'warning'
        );
      } else {
        showToast(`Erasure failed: ${err.message}`, 'error');
      }
    },
  });

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchTerm.trim().length >= 2) {
      setSearchQuery(searchTerm.trim());
      setSelectedCandidateId(null);
      setCertificate(null);
    }
  };

  const totalDataCount = preview
    ? Object.values(preview.dataCounts).reduce((sum, v) => sum + v, 0)
    : 0;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
          GDPR Erasure Requests
        </h1>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          Process right to erasure requests under GDPR Article 17
        </p>
      </div>

      {/* Search */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <Search className="h-5 w-5 text-purple-600" />
          Search Candidate
        </h2>
        <form onSubmit={handleSearch} className="flex gap-3">
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search by name or email (min 2 characters)"
            className="flex-1 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-4 py-2 text-sm text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-purple-500 focus:border-transparent"
          />
          <button
            type="submit"
            disabled={searchTerm.trim().length < 2 || searching}
            className="px-4 py-2 bg-purple-600 text-white rounded-lg text-sm font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Search className="h-4 w-4" />
            Search
          </button>
        </form>

        {/* Search Results */}
        {searching && (
          <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            Searching...
          </div>
        )}
        {searchResults?.data && searchResults.data.length > 0 && (
          <div className="mt-4 space-y-2">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {searchResults.data.length} candidate{searchResults.data.length !== 1 ? 's' : ''} found
            </p>
            {searchResults.data.map((candidate) => (
              <div
                key={candidate.id}
                className={`flex items-center justify-between p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedCandidateId === candidate.id
                    ? 'border-purple-500 bg-purple-50 dark:bg-purple-900/20'
                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700/50'
                }`}
                onClick={() => {
                  setSelectedCandidateId(candidate.id);
                  setCertificate(null);
                }}
              >
                <div className="flex items-center gap-3">
                  <User className="h-5 w-5 text-gray-400" />
                  <div>
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {candidate.firstName} {candidate.lastName}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {candidate.email}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs text-gray-500 dark:text-gray-400">
                  <span className="flex items-center gap-1">
                    <Briefcase className="h-3 w-3" />
                    {candidate.applicationCount} app{candidate.applicationCount !== 1 ? 's' : ''}
                  </span>
                  <span>{new Date(candidate.createdAt).toLocaleDateString()}</span>
                  <button
                    className="text-purple-600 hover:text-purple-700 font-medium"
                    onClick={(e) => {
                      e.stopPropagation();
                      setSelectedCandidateId(candidate.id);
                      setCertificate(null);
                    }}
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
        {searchResults?.data && searchResults.data.length === 0 && (
          <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">
            No candidates found matching &quot;{searchQuery}&quot;
          </p>
        )}
      </div>

      {/* Data Preview */}
      {selectedCandidateId && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <FileText className="h-5 w-5 text-purple-600" />
            Data Preview
          </h2>
          {loadingPreview ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">Loading data preview...</p>
          ) : preview ? (
            <div className="space-y-4">
              {/* Candidate info */}
              <div className="p-4 bg-gray-50 dark:bg-gray-700/50 rounded-lg">
                <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  {preview.candidate.firstName} {preview.candidate.lastName}
                </h3>
                <div className="mt-1 text-xs text-gray-500 dark:text-gray-400 space-y-0.5">
                  <p>Email: {preview.candidate.email}</p>
                  {preview.candidate.phone && <p>Phone: {preview.candidate.phone}</p>}
                  {preview.candidate.location && <p>Location: {preview.candidate.location}</p>}
                  <p>SR ID: {preview.candidate.srCandidateId}</p>
                  <p>Created: {new Date(preview.candidate.createdAt).toLocaleDateString()}</p>
                </div>
              </div>

              {/* Data counts */}
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {[
                  { label: 'Applications', count: preview.dataCounts.applications, icon: Briefcase },
                  { label: 'Reviews', count: preview.dataCounts.reviews, icon: FileText },
                  { label: 'PII Records', count: preview.dataCounts.piiVaultRecords, icon: Shield },
                  { label: 'Embeddings', count: preview.dataCounts.embeddings, icon: Eye },
                  { label: 'Similarity Scores', count: preview.dataCounts.similarityScores, icon: Eye },
                  { label: 'Queue Entries', count: preview.dataCounts.queueEntries, icon: Eye },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="p-3 bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30 rounded-lg text-center"
                  >
                    <p className="text-2xl font-bold text-red-700 dark:text-red-400">{item.count}</p>
                    <p className="text-xs text-red-600 dark:text-red-500">{item.label}</p>
                  </div>
                ))}
              </div>

              {/* Warnings */}
              {preview.warnings.length > 0 && (
                <div className="p-3 bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-lg">
                  <p className="text-sm font-medium text-amber-800 dark:text-amber-400 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    Warnings
                  </p>
                  <ul className="mt-1 text-xs text-amber-700 dark:text-amber-500 list-disc list-inside space-y-0.5">
                    {preview.warnings.map((w, i) => (
                      <li key={i}>{w}</li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Action */}
              <div className="flex items-center justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  Total: <strong>{totalDataCount}</strong> records will be permanently deleted
                </p>
                <button
                  onClick={() => setShowConfirmDialog(true)}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 flex items-center gap-2"
                >
                  <Trash2 className="h-4 w-4" />
                  Process Erasure Request
                </button>
              </div>
            </div>
          ) : null}
        </div>
      )}

      {/* Confirmation Dialog */}
      {showConfirmDialog && preview && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full mx-4 p-6">
            <div className="text-center mb-4">
              <div className="mx-auto w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mb-3">
                <AlertTriangle className="h-6 w-6 text-red-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Confirm GDPR Erasure
              </h3>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-2">
                This will permanently delete all data for{' '}
                <strong>{preview.candidate.firstName} {preview.candidate.lastName}</strong>.
                This action cannot be undone.
              </p>
            </div>

            <div className="bg-red-50 dark:bg-red-900/10 border border-red-200 dark:border-red-800/30 rounded-lg p-3 mb-4">
              <p className="text-xs font-medium text-red-800 dark:text-red-400">
                {totalDataCount} records across {Object.values(preview.dataCounts).filter(v => v > 0).length} data categories
              </p>
            </div>

            {forceDelete && (
              <div className="bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800/30 rounded-lg p-3 mb-4">
                <p className="text-xs text-amber-700 dark:text-amber-400">
                  Force delete enabled — active applications will also be deleted.
                </p>
              </div>
            )}

            <div className="flex gap-3">
              <button
                onClick={() => {
                  setShowConfirmDialog(false);
                  setForceDelete(false);
                }}
                className="flex-1 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700"
              >
                Cancel
              </button>
              <button
                onClick={() => erasureMutation.mutate()}
                disabled={erasureMutation.isPending}
                className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg text-sm font-medium hover:bg-red-700 disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {erasureMutation.isPending ? (
                  'Deleting...'
                ) : (
                  <>
                    <Trash2 className="h-4 w-4" />
                    Delete All Data
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Deletion Certificate */}
      {certificate && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6 border-l-4 border-green-500">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            Deletion Certificate
          </h2>
          <div className="space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-xs">Certificate ID</p>
                <p className="font-mono text-gray-900 dark:text-gray-100 text-xs">{certificate.certificateId}</p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-xs">Request ID</p>
                <p className="font-mono text-gray-900 dark:text-gray-100 text-xs">{certificate.deletionRequestId}</p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-xs">Requested</p>
                <p className="text-gray-900 dark:text-gray-100">{new Date(certificate.requestedAt).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-xs">Completed</p>
                <p className="text-gray-900 dark:text-gray-100">{new Date(certificate.completedAt).toLocaleString()}</p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-xs">Total Records Deleted</p>
                <p className="text-xl font-bold text-green-600">{certificate.deletionSummary.totalRecords}</p>
              </div>
              <div>
                <p className="text-gray-500 dark:text-gray-400 text-xs">Status</p>
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400">
                  {certificate.status}
                </span>
              </div>
            </div>
            <div>
              <p className="text-gray-500 dark:text-gray-400 text-xs mb-1">Verification Hash</p>
              <p className="font-mono text-xs text-gray-600 dark:text-gray-400 break-all bg-gray-50 dark:bg-gray-700/50 p-2 rounded">
                {certificate.verificationHash}
              </p>
            </div>
            <div className="flex gap-2 pt-2">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(JSON.stringify(certificate, null, 2));
                  showToast('Certificate copied to clipboard', 'success');
                }}
                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1"
              >
                <Copy className="h-3 w-3" /> Copy JSON
              </button>
              <button
                onClick={() => {
                  const blob = new Blob([JSON.stringify(certificate, null, 2)], { type: 'application/json' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url;
                  a.download = `gdpr-certificate-${certificate.certificateId}.json`;
                  a.click();
                  URL.revokeObjectURL(url);
                }}
                className="px-3 py-1.5 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg text-xs font-medium hover:bg-gray-50 dark:hover:bg-gray-700 flex items-center gap-1"
              >
                <Download className="h-3 w-3" /> Download
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Erasure Audit Log */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4 flex items-center gap-2">
          <Shield className="h-5 w-5 text-purple-600" />
          Erasure History
        </h2>
        {loadingAudits ? (
          <p className="text-sm text-gray-500 dark:text-gray-400">Loading audit log...</p>
        ) : audits?.data && audits.data.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200 dark:border-gray-700">
                    <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Date</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Email Hash</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Reason</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Records</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Status</th>
                    <th className="text-left py-2 px-3 text-xs font-medium text-gray-500 dark:text-gray-400 uppercase">Processed By</th>
                  </tr>
                </thead>
                <tbody>
                  {audits.data.map((audit) => (
                    <tr key={audit.id} className="border-b border-gray-100 dark:border-gray-700/50">
                      <td className="py-2 px-3 text-gray-900 dark:text-gray-100">
                        {new Date(audit.requestedAt).toLocaleDateString()}
                      </td>
                      <td className="py-2 px-3 font-mono text-xs text-gray-600 dark:text-gray-400">
                        {audit.candidateEmailHash.slice(0, 12)}...
                      </td>
                      <td className="py-2 px-3 text-gray-600 dark:text-gray-400">
                        {audit.reason.replace(/_/g, ' ')}
                      </td>
                      <td className="py-2 px-3 font-medium text-gray-900 dark:text-gray-100">
                        {audit.totalRecordsDeleted}
                      </td>
                      <td className="py-2 px-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          audit.status === 'completed'
                            ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-400'
                            : audit.status === 'failed'
                            ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-400'
                            : 'bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-400'
                        }`}>
                          {audit.status}
                        </span>
                      </td>
                      <td className="py-2 px-3 text-xs text-gray-500 dark:text-gray-400">
                        {audit.requestedByEmail || 'System'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Pagination */}
            {audits.pagination && (
              <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  Page {audits.pagination.page} of {audits.pagination.totalPages} ({audits.pagination.total} total)
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setAuditPage((p) => Math.max(1, p - 1))}
                    disabled={auditPage <= 1}
                    className="p-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-30"
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </button>
                  <button
                    onClick={() => setAuditPage((p) => p + 1)}
                    disabled={auditPage >= (audits.pagination?.totalPages ?? 1)}
                    className="p-1 rounded border border-gray-300 dark:border-gray-600 disabled:opacity-30"
                  >
                    <ChevronRight className="h-4 w-4" />
                  </button>
                </div>
              </div>
            )}
          </>
        ) : (
          <p className="text-sm text-gray-500 dark:text-gray-400">No erasure requests processed yet.</p>
        )}
      </div>
    </div>
  );
}
